import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Firm as ApiFirm, FirmsService } from 'api';
import { firmsAction } from 'common/actions';
import { useStore } from 'common/store';
import { Firm as StoreFirm } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { useResponse } from 'services/hooks/useResponse';

export type FirmFeatureSettingsUpdate = Pick<
  ApiFirm,
  | 'requires_2fa'
  | 'show_process_management_column'
  | 'are_fund_investments_enabled'
  | 'daily_nav_enabled'
  | 'use_selected_number_format_for_inputs'
>;

export const useUpdateFirmFeatureSettings = () => {
  const [{ firmInfo }, dispatch] = useStore() as unknown as UseStoreValues;
  const { processErrorResponse } = useResponse();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ firmId, data }: { firmId: number; data: FirmFeatureSettingsUpdate }) =>
      FirmsService.apiFirmsPartialUpdate(firmId, data as ApiFirm),
  });

  const updateFirmFeatureSettings = useCallback(
    async (firmId: number, data: FirmFeatureSettingsUpdate) => {
      try {
        const updatedFirm = await mutateAsync({ firmId, data });
        const selectedFirm = {
          ...(firmInfo?.id === firmId ? firmInfo : {}),
          ...updatedFirm,
          ...(firmInfo?.id === firmId ? { user_permissions: firmInfo.user_permissions } : {}),
        } as StoreFirm;

        dispatch(firmsAction.setFirmInfo(selectedFirm));

        const updatedFirmList = await firmsAction.getFirmList();
        if (Array.isArray(updatedFirmList?.firms)) {
          dispatch(firmsAction.setFirmList(updatedFirmList.firms));
        }

        return selectedFirm;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while saving the Feature Settings',
          action: 'save the Feature Settings',
        });
        return null;
      }
    },
    [dispatch, firmInfo, mutateAsync, processErrorResponse]
  );

  return { updateFirmFeatureSettings, isPending };
};
